import { useStore } from '@tanstack/react-form';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../../lib/env';
import {
  parseResponseErrors,
  ResponseError,
} from '../../../lib/flotiq-client/response-errors';
import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { getTestProps } from '../../../lib/helpers';

import CopyButton from '../../../components/CopyButton/CopyButton';
import Tooltip from '../../../components/Tooltip/Tooltip';

import { useSpace } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';
import useSectionSave from '../../../hooks/useSectionSave';
import useSelectedSpace from '../../../hooks/useSelectedSpace';

import { CopyIcon, QuestionMarkCircleIcon } from '../../../images/shapes';

import { useAppForm } from '../../../form-hooks/form';
import SpaceSettingsSection from '../SpaceSettingsSection/SpaceSettingsSection';

interface FormCollaborationSettingsProps {
  spaceName: string;
  testId?: string;
}

const SECTION_ID = 'realtime-collaboration-settings';

const FormCollaborationSettings = ({
  spaceName,
  testId,
}: FormCollaborationSettingsProps) => {
  const { t } = useTranslation();
  const { spaceData } = useSelectedSpace();

  const {
    entity: space,
    updateEntity: updateSpace,
    isLoading,
    errors: spaceErrors,
  } = useSpace(spaceData?.id, null);

  useApiErrorsToast(spaceErrors);

  const initialValues = useMemo(
    () => ({
      formCollaboration: space?.featureFlags?.formCollaboration || false,
    }),
    [space],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      try {
        const { body, status, ok } = await updateSpace({
          ...space,
          featureFlags: {
            ...space?.featureFlags,
            ...value,
          },
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        FlotiqPlugins.spaceFeatureFlags = body.featureFlags;
        toast.success(t('Spaces.Form.Updated'));
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'SupportIssueModal.Form.ErrorMessage'
              : 'Form.CommunicationErrorMessage',
          ),
        );
      }
    },
  });

  useEffect(() => {
    if (space) {
      form.reset(initialValues);
    }
  }, [space, form, initialValues, isLoading]);

  const handleSave = async () => {
    await form.handleSubmit();
  };

  const isFormDirty = useStore(form.store, (s) => s.isDirty);

  useSectionSave(SECTION_ID, handleSave, isFormDirty);

  return (
    <SpaceSettingsSection
      title={
        <span className="flex flex-col md:flex-row md:items-center gap-x-4 gap-y-2">
          <span className="text-3xl leading-tight">{spaceName}</span>

          {space?.id && (
            <span className="inline-flex items-center gap-3 text-lg font-normal leading-tight">
              <span>
                {t('SpaceSettings.SpaceId')}:{' '}
                <span className="font-bold">{space.id}</span>
              </span>

              <CopyButton
                text={space.id}
                onCopy={() => toast.success(t('SpaceSettings.CopiedSpaceId'))}
              >
                <CopyIcon
                  className={twMerge(
                    'w-3.5 shrink-0',
                    'opacity-50 hover:opacity-100',
                    'dark:text-slate-100',
                  )}
                  {...getTestProps(testId, 'copy-space-id')}
                />
              </CopyButton>
            </span>
          )}
        </span>
      }
    >
      {getEnv('VITE_COLLABORATION_ENDPOINT') && (
        <div className="mt-8">
          <form.AppForm>
            <form.AppField name="formCollaboration">
              {(field) => (
                <field.Switch
                  testId={testId}
                  label={
                    <Tooltip
                      tooltip={t('SpaceSettings.FormCollaboration.Tooltip')}
                      additionalClasses="inline-flex"
                    >
                      {t('SpaceSettings.FormCollaboration.label')}
                      <QuestionMarkCircleIcon className="text-blue w-3 ml-2" />
                    </Tooltip>
                  }
                />
              )}
            </form.AppField>
          </form.AppForm>
        </div>
      )}
    </SpaceSettingsSection>
  );
};

export default FormCollaborationSettings;
